import type { ConnectorMultiAgentConfig } from '../services/unifiedWebSocket';
import {
  buildEvaluatorParamsPayload,
  normalizeConfigTuningEvaluationMode,
  parseEvaluatorParamsForm,
  type ConfigTuningEvaluationMode,
} from './evaluation';
import { isMutationsApproach, normalizeOptimizationApproach } from './optimizationApproach';

export type SelectionScenario = 'quality' | 'tokens' | 'cost';
export type EvaluationMode = ConfigTuningEvaluationMode;

export {
  DEFAULT_EVALUATION_MODE,
  CONFIG_TUNING_EVALUATION_MODES as FRONTEND_EVALUATION_MODES,
  EVALUATION_MODE_LABELS,
  normalizeConfigTuningEvaluationMode as normalizeEvaluationMode,
  type EvaluatorParamsFormState,
  createDefaultEvaluatorParamsForm,
  mergeEvaluatorParamsFormForMode,
  parseEvaluatorParamsForm,
  buildEvaluatorParamsPayload,
  validateEvaluatorParamsForm,
} from './evaluation';

export const DEFAULT_SELECTION_SCENARIO: SelectionScenario = 'quality';

export function normalizeSelectionScenario(value: unknown): SelectionScenario {
  if (value === 'quality' || value === 'tokens' || value === 'cost') return value;
  return DEFAULT_SELECTION_SCENARIO;
}

export function deriveSelectionScenarioFromOptimizeByToken(optimizeByToken: unknown): SelectionScenario {
  return optimizeByToken === true ? 'tokens' : 'quality';
}

export function deriveOptimizeByTokenFromSelectionScenario(scenario: SelectionScenario): boolean {
  return scenario === 'tokens';
}

export function resolveSelectionScenario(params: {
  selection_scenario?: unknown;
  optimize_by_token?: unknown;
}): SelectionScenario {
  if (params.selection_scenario !== undefined && params.selection_scenario !== null) {
    return normalizeSelectionScenario(params.selection_scenario);
  }
  return deriveSelectionScenarioFromOptimizeByToken(params.optimize_by_token);
}

export interface OptimizationJobCreateRequest {
  mode: 'multi';
  integration_id: string;
  config_label?: string;
  approach?: ConnectorMultiAgentConfig['approach'];
  selection_scenario: SelectionScenario;
  evaluation_mode: EvaluationMode;
  evaluator_params?: Record<string, unknown>;
  optimize_by_token: boolean;
  optimization_policy?: ConnectorMultiAgentConfig['optimization_policy'];
  config: ConnectorMultiAgentConfig['config'];
}

export function buildOptimizationJobApiBody(
  payload: ConnectorMultiAgentConfig
): OptimizationJobCreateRequest {
  const selection_scenario = resolveSelectionScenario(payload);
  const approach = normalizeOptimizationApproach(payload.approach);
  const evaluation_mode: EvaluationMode = isMutationsApproach(approach)
    ? 'config-scorer'
    : normalizeConfigTuningEvaluationMode(payload.evaluation_mode);
  const evaluatorParamsForm = parseEvaluatorParamsForm(payload.evaluator_params);
  const evaluator_params = buildEvaluatorParamsPayload(evaluation_mode, evaluatorParamsForm, {
    includeDirection: true,
  });

  return {
    mode: 'multi',
    integration_id: payload.integration_id,
    ...(payload.config_label?.trim() ? { config_label: payload.config_label.trim() } : {}),
    approach: payload.approach,
    selection_scenario,
    evaluation_mode,
    ...(Object.keys(evaluator_params).length > 0 ? { evaluator_params } : {}),
    optimize_by_token: deriveOptimizeByTokenFromSelectionScenario(selection_scenario),
    ...(payload.optimization_policy ? { optimization_policy: payload.optimization_policy } : {}),
    config: payload.config,
  };
}
